import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { useLocation } from "react-router-dom";
//#region types
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
import { TimeReportViewProps } from "models/propTypes/timeReportingPropTypes";
import { RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import TimeReportingDetailsView from "../weeklyTimeReport/detailsView";
import TimeReportingMonthDetailsView from "../monthlyTimeReport/detailsView";
import { BreadCrumb, LoadingWithLogo, PageLayouts } from "components/common";
import { ErrorView } from "components/error";
import { useCompanyService, useTimeReportingService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

interface LoadedPeriod {
  key: string;
  data?: WintSuperkollAPIModelTimeReporting.TimeReportMetaDataWithPeriods;
}

// Rendered by both /TimeReport/Weekly and /TimeReport/Monthly, each passing its period. Being the same component on
// both routes, it stays mounted when the Week | Month toggle switches between them, so the full-screen dialog stays
// open and only its content changes, instead of one dialog closing and the next sliding up.
const TimeReportView: React.FC<TimeReportViewProps> = ({ period }) => {
  const companyService = useCompanyService();
  const timeReportingService = useTimeReportingService();
  const location = useLocation();

  const loggedInUser = useSelector((state: AppState) => state.auth.loggedInUser);
  const [loaded, setLoaded] = React.useState<LoadedPeriod>();
  const latestRequestKey = React.useRef<string>();

  const queryParams = new URLSearchParams(location.search);
  const personId = queryParams.has(RouteConstants.TimeReportingQueryParams.EmployeeId)
    ? Number(queryParams.get(RouteConstants.TimeReportingQueryParams.EmployeeId))
    : loggedInUser.personId;
  const requestKey = `${period}|${location.search}`;

  React.useEffect(() => {
    latestRequestKey.current = requestKey;
    const currentDate = queryParams.get(RouteConstants.TimeReportingQueryParams.CurrentDate);
    const date = currentDate ? DateHelper.parseLocalDate(currentDate) : new Date();
    const [start, end] =
      period === "month"
        ? [DateHelper.getFirstDayOfMonth(new Date(date)), DateHelper.getLastDayOfMonth(date)]
        : [DateHelper.getFirstDayOfWeek(date), DateHelper.getLastDayOfWeek(date)];

    timeReportingService
      .filter(personId, start, end)
      .catch(() => undefined)
      .then((res) => {
        // A quick second switch must not be overwritten by the first switch's slower response.
        if (latestRequestKey.current === requestKey) {
          setLoaded({ key: requestKey, data: res });
        }
      });
  }, [requestKey]);

  const hasPermission = React.useMemo(
    () => !!personId && companyService.permissionToModifyOtherPersonsTimeReports(personId),
    [personId]
  );

  // Until this period's data has arrived, the previous period's data must not reach the other view's details.
  const data = loaded?.key === requestKey ? loaded.data : undefined;
  const isLoading = loaded?.key !== requestKey;

  return (
    <PageLayouts.EntityFullScreen
      id={0}
      entityKey={`timeReporting`}
      navigation={
        <BreadCrumb
          prevItems={[
            {
              text: data?.employeeName ?? "",
              href: UrlHelper.formatPath(`/Employee/${data?.employeeId}`),
            },
          ]}
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.TimeReport)}
          showHome
        />
      }
      hasPermissions={hasPermission}
    >
      {isLoading ? (
        <LoadingWithLogo />
      ) : !data ? (
        <ErrorView />
      ) : period === "month" ? (
        <TimeReportingMonthDetailsView metaData={data} />
      ) : (
        <TimeReportingDetailsView metaData={data} />
      )}
    </PageLayouts.EntityFullScreen>
  );
};

export default TimeReportView;
